import React, { useEffect, useState, useCallback } from "react";
import axiosInstance from "../../api/axiosInstance";

import {
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress
} from "@mui/material";

import ProductDrawer from "../common/ProductDrawer";
import "./ProductsAdmin.css";
import { toast } from "react-toastify";

function ProductsAdmin() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [productId, setProductId] = useState(null);

  const [colors, setColors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [products, setProducts] = useState([]);

  const limit = 10;
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    category: [],
    brand: [],
    bikeModel: [],
    title: "",
    details: "",
    sizes: [{ size: "", price: "" }],
    selectedColors: [],
    mainImage: null,
    extraImages: [],
    existingMainImage: null,
    existingExtraImages: [],
    deletedImages: []
  });
  // ================= INITIAL LOAD =================
  useEffect(() => {
    loadInitialData();
    loadProducts(0, true);
  }, []);

  const loadInitialData = async () => {
    try {
      const [catRes, colorRes] = await Promise.all([
        axiosInstance.get("categories/"),
        axiosInstance.get("colors/")
      ]);

      setCategories(catRes.data.results || catRes.data);
      setColors(colorRes.data.results || colorRes.data);
    } catch (err) {
      console.error(err);
    }
  };

  // ================= PRODUCTS LOAD =================
  const loadProducts = useCallback(
    async (newOffset = 0, reset = false) => {
      if (loading) return;

      setLoading(true);
      try {
        const res = await axiosInstance.get(
          `products/?limit=${limit}&offset=${newOffset}`
        );

        const newProducts = res.data.results || [];

        if (reset) setProducts(newProducts);
        else setProducts((prev) => [...prev, ...newProducts]);

        if (newProducts.length < limit) setHasMore(false);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    },
    [limit, loading]
  );

  // ================= INFINITE SCROLL =================
  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop + 100 >=
        document.documentElement.offsetHeight
      ) {
        if (hasMore && !loading) {
          const newOffset = offset + limit;
          setOffset(newOffset);
          loadProducts(newOffset);
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [offset, hasMore, loading, loadProducts]);

  // ================= LOAD BRANDS =================
  const loadBrands = async (categoryIds) => {
    if (!categoryIds || categoryIds.length === 0) {
      setBrands([]);   // ✅ clear
      return;
    }

    const params = new URLSearchParams();
    categoryIds.forEach((id) => params.append("category", id));

    const res = await axiosInstance.get(`brands/?${params.toString()}`);

    setBrands(res.data.results || res.data);
  };
  // ================= LOAD MODELS =================
  const loadModels = async (brandIds) => {
    if (!brandIds || brandIds.length === 0) {
      setModels([]);
      return;
    }

    const params = new URLSearchParams();
    brandIds.forEach((id) => params.append("brand", id));

    const res = await axiosInstance.get(
      `bike-models/?${params.toString()}`
    );

    setModels(res.data.results || res.data);
  };
  // ================= ADD =================
  const openAdd = () => {
    setProductId(null);
    setForm({
      category: [],
      brand: [],
      bikeModel: [],
      title: "",
      details: "",
      selectedColors: [],
      mainImage: null,
      extraImages: [],
      existingMainImage: null,
      existingExtraImages: [],
      deletedImages: [],
      sizes: [{ size: "", price: "" }]   // ✅ FIXED
    });
    setDrawerOpen(true);
  };

  // ================= EDIT =================
  const openEdit = async (p) => {
    const categoryIds = p.category?.map((c) => c.id) || [];
    const brandIds = p.brand?.map((b) => b.id) || [];

    if (categoryIds.length) await loadBrands(categoryIds);
    if (brandIds.length) await loadModels(brandIds);

    setProductId(p.id);

    setForm({
      category: categoryIds,
      brand: brandIds,
      bikeModel: p.bike_model?.map((m) => m.id) || [],
      title: p.product_title || "",
      details: p.product_details || "",
      selectedColors: p.colors?.map((c) => c.id) || [],
      existingMainImage: p.product_img,
      existingExtraImages: p.images || [],
      mainImage: null,
      extraImages: [],
      deletedImages: [],
      sizes: p.sizes || [{ size: "", price: "" }],
    });

    setDrawerOpen(true);
  };

  // ================= DELETE =================
  const handleDelete = async (id) => {
    if (!window.confirm("Delete product?")) return;
    await axiosInstance.delete(`products/?id=${id}`);
    setOffset(0);
    setHasMore(true);
    loadProducts(0, true);
  };

  const handleSubmit = async (formDataFromDrawer) => {
    try {
      if (productId)
        await axiosInstance.put(
          `products/?id=${productId}`,
          formDataFromDrawer
        );
      else
        await axiosInstance.post("products/", formDataFromDrawer);

      setDrawerOpen(false);
      setOffset(0);
      setHasMore(true);
      loadProducts(0, true);
    } catch (err) {
      console.error(err);
      toast.error("Save failed ❌");
    }
  };
  return (
    <Box className="pa-container">
      {/* HEADER */}
      <div className="pa-header">
        <h2 className="pa-title">Products</h2>
        <button className="pa-add-btn" onClick={openAdd}>
          + Add Product
        </button>
      </div>

      {/* GRID */}
      <Grid container spacing={3} className="pa-grid">
        {products.map((p) => (
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            lg={3}
            key={p.id}
            className="pa-grid-item"
          >
            <Card className="pa-card">
              <div
                className="pa-image"
                style={{
                  backgroundImage: `url(${p.product_img})`
                }}
              />

              <CardContent className="pa-content">
                <Typography className="pa-product-title" noWrap>
                  {p.product_title}
                </Typography>

                <Typography className="pa-product-price">
                  ₹ {p.sizes?.[0]?.price || 0}
                </Typography>

                {/* COLORS */}
                <Box className="pa-color-container">
                  {p.colors?.map((c) => (
                    <Box
                      key={c.id}
                      className="pa-color-circle"
                      sx={{ backgroundColor: c.hex_code }}
                    />
                  ))}
                </Box>

                {/* ACTION BUTTONS */}
                <div className="pa-actions">
                  <Button
                    size="small"
                    variant="contained"
                    className="pa-edit-btn"
                    onClick={() => openEdit(p)}
                  >
                    Edit
                  </Button>

                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    className="pa-delete-btn"
                    onClick={() => handleDelete(p.id)}
                  >
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {loading && (
        <Box textAlign="center" mt={4}>
          <CircularProgress />
        </Box>
      )}

      <ProductDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSubmit={handleSubmit}
        productId={productId}
        categories={categories}
        brands={brands}
        models={models}
        colors={colors}
        form={form}
        setForm={setForm}
        loadBrands={loadBrands}
        loadModels={loadModels}
      />
    </Box>
  );
}

export default ProductsAdmin;